import { useMemo, useState, useEffect } from 'react';
import {
  Megaphone,
  Search,
  Info,
  Pin,
  Clock,
  Building2,
  Landmark,
} from 'lucide-react';
import { useCrud } from '../../api/hooks/useCrud';
import { ComunicadoLeituraDetail } from '../../components/comunicados/ComunicadoLeituraDetail';
import type { ComunicadoLeitura } from '../../components/comunicados/ComunicadoLeituraSheet';
import {
  COMUNICADO_TIPOS,
  COMUNICADO_TIPO_COLORS,
  COMUNICADO_TIPO_ICON,
  DESTINATARIO_LABEL,
} from '../../components/comunicados/comunicados-shared';
import './comunicados-professor-desktop.css';
import './professor-dashboard-desktop.css';

interface Comunicado extends ComunicadoLeitura {
  turmaId?: string;
  ativo: boolean;
}

function formatDateShort(d: string) {
  try {
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  } catch {
    return d;
  }
}

function origemLabel(c: Comunicado) {
  if (c.municipio?.nome) return `Prefeitura · ${c.municipio.nome}`;
  return c.escola?.nome || '';
}

export function ComunicadosProfessorDesktop() {
  const { data, isLoading } = useCrud<Comunicado>('comunicados');
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [selecionadoId, setSelecionadoId] = useState<string | null>(null);

  const ativos = useMemo(() => data.filter((c) => c.ativo), [data]);

  const tipoStats = useMemo(
    () =>
      COMUNICADO_TIPOS.map((t) => ({
        tipo: t,
        count: ativos.filter((c) => c.tipo === t).length,
        ...COMUNICADO_TIPO_COLORS[t],
        ...COMUNICADO_TIPO_ICON[t],
      })),
    [ativos],
  );

  const filteredRows = useMemo(() => {
    let rows = ativos;
    if (filtroTipo) rows = rows.filter((c) => c.tipo === filtroTipo);
    const q = busca.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (c) =>
          c.titulo.toLowerCase().includes(q) ||
          c.corpo?.toLowerCase().includes(q) ||
          c.tipo.toLowerCase().includes(q) ||
          (DESTINATARIO_LABEL[c.destinatario] || c.destinatario).toLowerCase().includes(q),
      );
    }
    return [...rows].sort((a, b) => {
      if (a.fixado !== b.fixado) return a.fixado ? -1 : 1;
      return new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime();
    });
  }, [ativos, busca, filtroTipo]);

  const fixados = useMemo(
    () => ativos.filter((c) => c.fixado).sort((a, b) => new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime()),
    [ativos],
  );

  const selecionado = useMemo(
    () => filteredRows.find((c) => c.id === selecionadoId) ?? null,
    [filteredRows, selecionadoId],
  );

  useEffect(() => {
    if (filteredRows.length === 0) {
      setSelecionadoId(null);
      return;
    }
    if (!selecionadoId || !filteredRows.some((c) => c.id === selecionadoId)) {
      setSelecionadoId(filteredRows[0].id);
    }
  }, [filteredRows, selecionadoId]);

  return (
    <div className="admin-page text-[13px] leading-snug dash-desk-page com-desk-page">
      <div className="dash-desk-root com-desk-root">
        <div className="dash-desk-top com-desk-top">
          <div className="dash-desk-greeting">
            <h1 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
              Mural de comunicados
            </h1>
            <p className="com-desk-subtitle">Oficial da escola · somente leitura</p>
          </div>
          <div className="dash-desk-actions" aria-hidden>
            <Megaphone size={18} style={{ color: '#2563eb', opacity: 0.85 }} />
          </div>
        </div>

        <div className="com-desk-metrics">
          {tipoStats.map((t) => {
            const { Icon, label, color, bg, border, tipo, count } = t;
            const active = filtroTipo === tipo;
            return (
              <button
                key={tipo}
                type="button"
                className={`com-desk-kpi-btn ${active ? 'com-desk-kpi-btn--active' : ''}`}
                onClick={() => setFiltroTipo(active ? '' : tipo)}
                aria-pressed={active}
                title={label}
              >
                <div
                  className="dash-desk-kpi"
                  style={{ background: bg, border: `1px solid ${border}` }}
                >
                  <div className="dash-desk-kpi-icon" style={{ background: `${color}18`, color }}>
                    <Icon size={14} />
                  </div>
                  <div>
                    <div className="dash-desk-kpi-val" style={{ color }}>
                      {count}
                    </div>
                    <div className="dash-desk-kpi-lbl" style={{ color }}>
                      {label}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="com-desk-notice">
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Visualização do mural institucional. Para avisar a família de um aluno, use{' '}
            <strong>Meus Alunos → Avisar responsável</strong>.
          </span>
        </div>

        <div className="com-desk-toolbar">
          <div className="com-desk-search-wrap">
            <Search size={14} />
            <input
              type="search"
              className="com-desk-search"
              placeholder="Buscar título, conteúdo, tipo…"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <span className="com-desk-count">{filteredRows.length} comunicado(s)</span>
        </div>

        <div className="com-desk-body">
          <div className="com-desk-list-card">
            {fixados.length > 0 && (
              <div className="com-desk-fixados">
                <p className="com-desk-fixados-title">
                  <Pin size={11} /> Fixados
                </p>
                <div className="com-desk-fixados-list">
                  {fixados.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={`com-desk-fixado-chip ${selecionadoId === c.id ? 'com-desk-fixado-chip--active' : ''}`}
                      onClick={() => setSelecionadoId(c.id)}
                    >
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.titulo}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="com-desk-list-scroll">
              {isLoading ? (
                <p className="com-desk-loading">Carregando comunicados…</p>
              ) : filteredRows.length === 0 ? (
                <p className="com-desk-empty-list">Nenhum comunicado encontrado.</p>
              ) : (
                filteredRows.map((c) => {
                  const tc = COMUNICADO_TIPO_COLORS[c.tipo] || {
                    bg: '#f3f4f6',
                    color: '#6b7280',
                    border: '#e5e7eb',
                  };
                  const origem = origemLabel(c);
                  const active = selecionadoId === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={[
                        'com-desk-list-item',
                        active ? 'com-desk-list-item--active' : '',
                        c.tipo === 'URGENTE' ? 'com-desk-list-item--urgente' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      style={{ borderLeftColor: tc.color }}
                      onClick={() => setSelecionadoId(c.id)}
                    >
                      <p className="com-desk-list-title">
                        {c.fixado && <Pin size={10} style={{ color: '#d97706', flexShrink: 0 }} />}
                        <span>{c.titulo}</span>
                      </p>
                      <div className="com-desk-list-meta">
                        <span className="com-desk-tag" style={{ background: tc.bg, color: tc.color }}>
                          {c.tipo}
                        </span>
                        <span className="com-desk-tag com-desk-tag--dest">
                          {DESTINATARIO_LABEL[c.destinatario] || c.destinatario}
                        </span>
                        {Boolean((c as any).configPreview?.midiaUrl || (c as any).midiaUrl || (c as any).imagemUrl) && (
                          <span className="com-desk-tag" style={{ background: '#eff6ff', color: '#2563eb', fontWeight: 700 }}>
                            COM FOLDER
                          </span>
                        )}
                        <span className="com-desk-list-date">
                          <Clock size={9} /> {formatDateShort(c.criadoEm)}
                        </span>
                      </div>
                      {c.corpo && <p className="com-desk-list-preview">{c.corpo}</p>}
                      {origem && (
                        <p className="com-desk-list-preview" style={{ marginTop: 2 }}>
                          {c.municipio?.nome ? (
                            <Landmark size={9} style={{ display: 'inline', marginRight: 3, verticalAlign: -1 }} />
                          ) : (
                            <Building2 size={9} style={{ display: 'inline', marginRight: 3, verticalAlign: -1 }} />
                          )}
                          {origem}
                        </p>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div className="com-desk-detail-card">
            <div className="com-desk-detail-scroll">
              <ComunicadoLeituraDetail comunicado={selecionado} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
